import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useSchoolPasses } from '../../../hooks/useSchoolPasses';
import { useClassSections } from '../../../hooks/useClassSections';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { PASS_STATUS, listPasses } from '../../../api/studentPassesApi';
import { getTodaySchedule } from '../../../api/schoolConfigApi';
import { showError } from '../../../utils/notifications';
import { getContextualError, logError } from '../../../utils/errorHandling';
import CollapsibleSection from '../../common/CollapsibleSection';
import ConnectionStatusBanner from '../../common/ConnectionStatusBanner';
import SearchFilter from '../../common/SearchFilter';
import { matchesPassQuery } from '../../../utils/passSearch';
import type { Pass } from '../../../hooks/usePassSocket';
import type { components } from '../../../api/types.gen';
import styles from './ActivePassesPanel.module.css';
import adminStyles from '../AdminPanel.module.css';

type PeriodWindowResponse = components['schemas']['PeriodWindowResponse'];

interface ActivePassesPanelProps {
  schoolId?: number | string;
}

interface TodayStats {
  total: number | null;
  denied: number | null;
}

/** Reports whether an in-flight fetch has been superseded/cancelled. */
type IsCancelled = () => boolean;

// Ported from AdminPanel.js's "Today's Pass Activity" CollapsibleSection
// (JSX lines 3256-3343) and its 'total'/per-period tab state (activeTab,
// line 61; getFilteredPasses/todaysPeriods, lines 2711-2764 and 250-254) as
// part of the Firebase -> hallpass-backend migration (Admin step 8).
//
// The legacy "System Statistics" stat-card grid (lines 3085-3109) is now
// re-added below as a "Today's Pass Activity" stats row, but re-scoped from
// all-time to today only — this board has no all-time pass history
// resident (see below), and all-time reporting still belongs to
// PassHistoryView. It uses a hybrid data source: "Currently Out" (ACTIVE)
// and "Queued" (WAITING) are derived from the live useSchoolPasses() state
// already described below (no extra request); "Total" and "Denied" require
// counting every status for the whole day, which the live open-passes feed
// doesn't carry, so those two are fetched once on mount/reconnect via
// listPasses({from, to}) scoped to the local calendar day (see
// fetchTodayStats below) and refreshed manually via the Refresh button.
//
// Data source swap: the legacy passes onSnapshot listener
// (organizationData/{orgId}/passes, AdminPanel.js line 322) streamed every
// pass ever created for the org, and this component locally filtered down
// to "today's passes". hallpass-backend has no equivalent all-time
// school-scoped feed — useSchoolPasses() (the same Socket.io + REST hook
// TeacherDashboard.js's live board uses) instead holds currently-open passes
// (PENDING/WAITING/ACTIVE) plus whatever terminal events arrive over the
// socket during this session, which is the closest available equivalent to
// a live "today's activity" board. Real-time push, upsert-by-id, and REST
// resync-on-connect all come for free from that hook; this component does
// not duplicate any of that logic.
//
// Period tabs: legacy pass documents carried a `currentPeriod` string
// matched with String#includes(); hallpass-backend pass objects carry no
// such field. Periods are derived instead from a pass's classSectionId ->
// that class section's periodName (via useClassSections(), the same
// resolution TeacherDashboard.js uses), matched by exact string equality
// against GET /schools/:schoolId/schedule/today's periods list
// (schoolConfigApi.js's getTodaySchedule), which replaces the legacy
// schedule-utils period derivation.
//
// processQueueIntegrityV2 and its UI were NOT ported here (confirmed
// removal from the step-1 migration manifest).

// Safety cap on the pagination loop below, mirroring ClassSectionsPanel.js's
// ALL_USERS_MAX_PAGES — guards against an infinite loop if the backend ever
// returns a non-advancing cursor.
const TODAY_STATS_MAX_PAGES = 200;

// Tab id for passes whose class section can't be resolved to a period —
// either the student has no enrollment (classSectionId === null, normal at a
// school that hasn't uploaded rosters) or the section was soft-deleted. They
// match no period tab by construction, so without this they were reachable
// only from 'total'. Not a period name, so it can never collide with one.
const UNASSIGNED_TAB = '__unassigned__';

const ActivePassesPanel = ({
  schoolId: schoolIdProp,
}: ActivePassesPanelProps = {}): React.ReactElement => {
  const { currentUser } = useAuth();
  const schoolId = schoolIdProp ?? currentUser?.schoolId;
  const isViewingDifferentSchool =
    schoolIdProp != null &&
    String(schoolIdProp) !== String(currentUser?.schoolId);

  const { passes, connectionStatus } = useSchoolPasses();
  const { classSections } = useClassSections();

  const [loadingStates, setLoading] = useLoadingStates({
    loadSchedule: false,
    loadTodayStats: false,
  });

  const [periods, setPeriods] = useState<PeriodWindowResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<string>('total');
  const [todayStats, setTodayStats] = useState<TodayStats>({
    total: null,
    denied: null,
  });

  // Guards against a stale response clobbering `periods` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchSchedule = useCallback(
    async (isCancelled: IsCancelled = () => false): Promise<void> => {
      if (!schoolId) return;

      if (!isCancelled()) setLoading('loadSchedule', true);
      try {
        const schedule = await getTodaySchedule(schoolId);
        if (!isCancelled()) setPeriods(schedule.periods);
      } catch (error) {
        logError(error, 'client_api', 'ActivePassesPanel:loadSchedule');
        if (!isCancelled()) {
          showError(
            getContextualError(
              error,
              undefined,
              "Failed to load today's schedule."
            )
          );
        }
      } finally {
        if (!isCancelled()) setLoading('loadSchedule', false);
      }
    },
    [schoolId, setLoading]
  );

  useEffect(() => {
    let cancelled = false;
    fetchSchedule(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchSchedule]);

  // Reset to the 'total' tab if the previously-selected period tab no
  // longer exists in today's schedule (e.g. schedule changed underneath us).
  useEffect(() => {
    if (
      activeTab !== 'total' &&
      activeTab !== UNASSIGNED_TAB &&
      !periods.some((period) => period.name === activeTab)
    ) {
      setActiveTab('total');
    }
  }, [periods, activeTab]);

  // Monotonic id for the in-flight fetchTodayStats run. The connect effect
  // below refetches on every 'connected' transition, so a flurry of rapid
  // reconnects (or a Refresh click landing mid-reconnect) can leave two
  // paginated stat fetches overlapping. Each run captures its id up front
  // and only applies its result/loading/error if it's still the newest —
  // an earlier, slower run that resolves late is dropped instead of
  // clobbering a newer run's counts. Last-write-wins, mirroring this file's
  // cancelled-flag pattern in fetchSchedule.
  const statsRequestId = useRef<number>(0);

  // Total/Denied counts for the whole local calendar day, across every
  // status — the live open-passes feed only ever holds PENDING/WAITING/
  // ACTIVE plus whatever terminal events happened to arrive this session,
  // so it can't answer "how many passes total/denied today". Boundaries
  // match PassHistoryView.js's established local-day pattern (setHours(0/23)
  // rather than UTC midnight).
  const fetchTodayStats = useCallback(async (): Promise<void> => {
    const requestId = statsRequestId.current + 1;
    statsRequestId.current = requestId;
    const isStale = (): boolean => requestId !== statsRequestId.current;

    setLoading('loadTodayStats', true);
    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      let todaysPasses: Pass[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < TODAY_STATS_MAX_PAGES; i++) {
        const page = await listPasses({
          from: todayStart.toISOString(),
          to: todayEnd.toISOString(),
          cursor,
          limit: 100,
        });
        todaysPasses = todaysPasses.concat(page.data);
        if (!page.nextCursor) break;
        cursor = page.nextCursor;
      }

      if (!isStale()) {
        setTodayStats({
          total: todaysPasses.length,
          denied: todaysPasses.filter(
            (pass) => pass.status === PASS_STATUS.DENIED
          ).length,
        });
      }
    } catch (error) {
      if (!isStale()) {
        logError(error, 'client_api', 'ActivePassesPanel:loadTodayStats');
        showError(
          getContextualError(
            error,
            undefined,
            "Failed to load today's pass stats."
          )
        );
      }
    } finally {
      // Only the newest run owns the shared loading flag: a stale run
      // finishing must not flip it off while the newest run is still going.
      if (!isStale()) setLoading('loadTodayStats', false);
    }
  }, [setLoading]);

  // Fetch on mount (first 'connected') and again on every socket reconnect —
  // this doesn't need to be real-time-live like the open-passes list, so no
  // polling; the Refresh button below covers on-demand updates.
  useEffect(() => {
    if (connectionStatus === 'connected') {
      fetchTodayStats();
    }
  }, [connectionStatus, fetchTodayStats]);

  const activeCount = useMemo(
    () => passes.filter((pass) => pass.status === PASS_STATUS.ACTIVE).length,
    [passes]
  );

  const waitingCount = useMemo(
    () => passes.filter((pass) => pass.status === PASS_STATUS.WAITING).length,
    [passes]
  );

  const periodNameByClassSectionId = useMemo(() => {
    const map = new Map<number | null, string>();
    classSections.forEach((cs) => map.set(cs.id, cs.periodName));
    return map;
  }, [classSections]);

  // Search narrows the whole board before the tabs partition it, so each tab
  // count describes what you'd actually see on that tab rather than a total
  // the search has already excluded.
  const searchedPasses = useMemo(
    () => passes.filter((pass) => matchesPassQuery(pass, searchQuery)),
    [passes, searchQuery]
  );

  // Passes with no resolvable period — drives whether the Unassigned tab is
  // offered at all, its count, and the tab's own contents below.
  const unassignedPasses = useMemo(
    () =>
      searchedPasses.filter(
        (pass) => periodNameByClassSectionId.get(pass.classSectionId) == null
      ),
    [searchedPasses, periodNameByClassSectionId]
  );

  const getFilteredPasses = useCallback((): Pass[] => {
    if (activeTab === 'total') {
      return searchedPasses;
    }
    if (activeTab === UNASSIGNED_TAB) {
      return unassignedPasses;
    }
    return searchedPasses.filter(
      (pass) =>
        periodNameByClassSectionId.get(pass.classSectionId) === activeTab
    );
  }, [searchedPasses, unassignedPasses, activeTab, periodNameByClassSectionId]);

  const filteredPasses = getFilteredPasses();

  const formatTime = (value: string | null): string | null =>
    value ? new Date(value).toLocaleTimeString() : null;

  return (
    <CollapsibleSection
      title="Active Passes"
      icon="📝"
      id="active-passes"
      defaultExpanded={true}
    >
      {isViewingDifferentSchool && (
        <p style={{ color: '#856404', fontWeight: 'bold' }}>
          ⚠️ You're viewing another school's schedule, but the live passes board,
          the class-section names, and the Total/Denied stats below still reflect
          your own school — they cannot yet follow the school switch.
        </p>
      )}

      <ConnectionStatusBanner connectionStatus={connectionStatus} />

      <div className={styles['stats-section']}>
        <h3>Today's Pass Activity</h3>
        <div className={styles['stats-grid']}>
          <div className={styles['stat-card']}>
            <h3>{todayStats.total ?? '—'}</h3>
            <p>Total</p>
          </div>
          <div className={styles['stat-card']}>
            <h3>{activeCount}</h3>
            <p>Currently Out</p>
          </div>
          <div className={styles['stat-card']}>
            <h3>{waitingCount}</h3>
            <p>Queued</p>
          </div>
          <div className={styles['stat-card']}>
            <h3>{todayStats.denied ?? '—'}</h3>
            <p>Denied</p>
          </div>
        </div>
        <button
          className={adminStyles['add-btn']}
          onClick={fetchTodayStats}
          disabled={loadingStates.loadTodayStats}
        >
          {loadingStates.loadTodayStats ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* 150ms rather than the 500ms default: this filters an in-memory list,
          so the longer delay is just dead typing. */}
      <SearchFilter
        value={searchQuery}
        onChange={setSearchQuery}
        debounceMs={150}
        placeholder="Search student or destination..."
        ariaLabel="Search active passes by student or destination"
      />

      <div className={styles['pass-tabs']}>
        <button
          className={`${styles['tab-button']} ${
            activeTab === 'total' ? styles['active'] : ''
          }`}
          onClick={() => setActiveTab('total')}
        >
          Total ({searchedPasses.length})
        </button>

        {periods.map((period) => {
          const periodPasses = searchedPasses.filter(
            (pass) =>
              periodNameByClassSectionId.get(pass.classSectionId) ===
              period.name
          );

          return (
            <button
              key={period.id}
              className={`${styles['tab-button']} ${
                activeTab === period.name ? styles['active'] : ''
              }`}
              onClick={() => setActiveTab(period.name)}
            >
              {period.name} ({periodPasses.length})
            </button>
          );
        })}

        {/* Rendered when such passes exist, so a fully-rostered school never
            sees an empty tab — but kept mounted while it's the ACTIVE tab so
            the board can't be stranded on a tab that disappears mid-typing
            (a query matching no unassigned pass) or when the last unassigned
            pass returns over the socket. The count then honestly reads 0. */}
        {(unassignedPasses.length > 0 || activeTab === UNASSIGNED_TAB) && (
          <button
            className={`${styles['tab-button']} ${
              activeTab === UNASSIGNED_TAB ? styles['active'] : ''
            }`}
            onClick={() => setActiveTab(UNASSIGNED_TAB)}
          >
            Unassigned ({unassignedPasses.length})
          </button>
        )}
      </div>

      <div className={styles['passes-list']}>
        {loadingStates.loadSchedule && periods.length === 0 ? (
          <p className={styles['no-passes']}>Loading today's schedule...</p>
        ) : filteredPasses.length === 0 ? (
          <p className={styles['no-passes']}>
            {/* With a query active the emptiness is the search's doing, not
                the tab's — say which filter produced it. */}
            {searchQuery.trim()
              ? `No passes match "${searchQuery.trim()}".`
              : 'No active passes for this period.'}
          </p>
        ) : (
          filteredPasses.map((pass) => (
            <div key={pass.id} className={styles['pass-summary']}>
              <div className={styles['pass-header']}>
                <strong>{pass.studentName}</strong>
                <span
                  className={`${styles['pass-status']} ${
                    styles[`status-${pass.status.toLowerCase()}`]
                  }`}
                >
                  {pass.status}
                </span>
              </div>
              <div className={styles['pass-details']}>
                <span className={styles['destination']}>
                  {pass.destinationName}
                </span>
                {(pass.classSectionId == null ||
                  periodNameByClassSectionId.get(pass.classSectionId)) && (
                  <span className={styles['period']}>
                    •{' '}
                    {pass.classSectionId == null
                      ? 'Unassigned'
                      : periodNameByClassSectionId.get(pass.classSectionId)}
                  </span>
                )}
              </div>
              <small className={styles['timestamp']}>
                Requested: {formatTime(pass.requestedAt)}
                {pass.status === PASS_STATUS.COMPLETED && pass.returnedAt && (
                  <>
                    <br />
                    Returned: {formatTime(pass.returnedAt)}
                    {pass.activatedAt && (
                      <>
                        {' '}
                        (
                        {Math.round(
                          (new Date(pass.returnedAt).getTime() -
                            new Date(pass.activatedAt).getTime()) /
                            60000
                        )}{' '}
                        min)
                      </>
                    )}
                  </>
                )}
              </small>
            </div>
          ))
        )}
      </div>
    </CollapsibleSection>
  );
};

export default ActivePassesPanel;
